import {
  ComponentMultiStyleConfig,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import "react-datepicker/dist/react-datepicker.css";
import { CMTimePicker } from "../../base";
import { isEqual } from "lodash";
import { timeToTimeText } from "../../../lib/util/common-date-util";
import { hasValue } from "../../../lib/util/common-util";

export type CMFormInputBaseTimeProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "error" | "diff";
  size?: "sm" | "md" | "lg";

  placeholder?: string;

  /**
   * 値。
   */
  value?: { hour: number; minute: number } | null;

  /**
   * 差分モード時の変更前の値。
   * 差分モードの場合のみ必須。
   */
  changedFrom?: { hour: number; minute: number } | null;

  /**
   * 呼び出し元に時刻情報を返却する。
   * @param value 入力または選択された時刻
   */
  onChange?: (value: { hour: number; minute: number } | null) => void;
  onBlur?: () => void;
} & ComponentStyleProps;

/**
 * 時刻選択コンポーネント.
 *
 * HH:mm形式の時刻が入力された場合は時刻を返却する。
 */
export const CMFormInputBaseTime = ({
  editMode = "editable",
  variant = "default",
  size = "md",

  placeholder,

  value = null,

  changedFrom = null,

  onChange,
  onBlur,

  sx,
  ...rest
}: CMFormInputBaseTimeProps) => {
  const styles = useMultiStyleConfig("CMFormInputBaseTimeStyles", {
    variant,
    size,
  });
  const isDiffMode = useMemo(() => variant === "diff", [variant]);

  const disabled = editMode === "disabled" || editMode === "readOnly";
  const isInvalid = variant === "error";

  /**
   * 差分モード時、値が変更前と異なるならtrue。
   * 以下のいずれかの場合はfalse。
   * - 差分モードではない。
   * - 変更前と値が同一である。
   */
  const hasChangedInDiffMode = useMemo(() => {
    if (!isDiffMode) return false;
    return !isEqual(value, changedFrom);
  }, [isDiffMode, value, changedFrom]);

  const inputStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );
  const readonlyStyles = useMemo(
    () => ({
      ...styles.readonly,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );

  return editMode === "readOnly" ? (
    <Text sx={readonlyStyles} {...rest}>
      {hasValue(value) ? timeToTimeText(value) : ""}
    </Text>
  ) : (
    <CMTimePicker
      size={size}
      disabled={disabled}
      isInvalid={isInvalid}
      placeholder={placeholder}
      selected={value}
      onChange={onChange}
      onBlur={onBlur}
      sx={inputStyles}
      {...rest}
    />
  );
};

export const CMFormInputBaseTimeStyles: ComponentMultiStyleConfig = {
  parts: ["containerBase", "readonly", "diff"],
  baseStyle: {
    containerBase: { bgColor: "white" },
    readonly: {
      maxW: "max-content",
    },
    diff: {
      borderWidth: "1px",
      borderColor: "orange.500",
      _hover: {
        borderColor: "orange.500",
      },
      _focus: {
        _hover: {
          borderColor: "blue.500",
        },
      },
    },
  },
  sizes: {
    sm: {
      containerBase: {
        h: "22px",
        px: "6px",
      },
    },
    md: {
      containerBase: {
        h: "30px",
        px: "8px",
      },
    },
    lg: {
      containerBase: {
        h: "38px",
        px: "10px",
      },
    },
  },
};
